// src/admin/legalDocumentsApi.js — the superadmin «Configurazione» → «TOS & Privacy» screen (v219): list the legal
// document versions, add one (multipart, with its PDF), correct one, download Brian's copy of a PDF.
// Contract: <BACKEND_FOLDER>/docs/api-server/endpoints.md#legal-documents-superadmin and
// <BACKEND_FOLDER>/docs/frontend/2026-10-30-legal-document-versions-screen-handoff.md (BE ≥ 6991a98).
//
// ⛔ `suppressTenantHeader` on every call: the routes are platform-wide, not tenant-scoped.
// ⛔ Bodies are FormData: apiFetch then sets no Content-Type, so the browser writes the multipart boundary.
// ⛔ Errors are buildApiError (err.code / err.status): worded by admin/legal/legalErrorCopy.js, never err.message.
// ⚠️ A server without the BE's nginx location answers a > 1 MB upload with an HTML 413 (no JSON): it still
// rejects with err.status 413, which the copy words as "file too large".
import { apiFetch } from '../auth/apiFetch.js';
import { buildApiError } from '../context/parseApiError.js';

const BASE = '/api/v1/admin/legal-documents';

// A missing segment is a programming error: it throws before anything reaches the network.
const seg = (value, what) => {
  if (typeof value !== 'string' || value === '') {
    throw new TypeError(`legalDocumentsApi: a ${what} is required`);
  }
  return encodeURIComponent(value);
};
const versionPath = (document, version) => `${BASE}/${seg(document, 'document')}/${seg(version, 'version')}`;

// FastAPI wraps a raised HTTPException detail in `{ detail: {...} }`; the handled envelope arrives flat.
async function call(path, init = {}, what = 'Request failed') {
  const res = await apiFetch(path, { ...init, suppressTenantHeader: true });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const envelope = body && typeof body.detail === 'object' && body.detail !== null ? body.detail : body;
    throw buildApiError(envelope, res.status, { fallbackMessage: `${what}: ${res.status}` });
  }
  return res;
}

/** `{ current, versions }` — see admin/legal/legalModel.js :: normaliseList. */
export async function listLegalDocuments() {
  const res = await call(BASE, {}, 'Failed to list legal documents');
  return res.json();
}

/** Add a version. `effectiveFrom` = an ISO date-time WITH offset, or null/undefined for "now" (omitted). */
export async function addLegalVersion({ document, version, url, pdfUrl, file, effectiveFrom }) {
  const form = new FormData();
  form.append('document', document);
  form.append('version', version);
  form.append('url', url);
  form.append('pdf_url', pdfUrl);
  if (effectiveFrom) form.append('effective_from', effectiveFrom);
  form.append('file', file);
  const res = await call(BASE, { method: 'POST', body: form }, 'Failed to add legal version');
  return res.json();
}

/** Correct a version: only the fields given are sent (url, pdfUrl, file). Nothing given = a programming error. */
export async function correctLegalVersion(document, version, { url, pdfUrl, file } = {}) {
  const path = versionPath(document, version);
  const form = new FormData();
  if (typeof url === 'string') form.append('url', url);
  if (typeof pdfUrl === 'string') form.append('pdf_url', pdfUrl);
  if (file) form.append('file', file);
  if (![...form.keys()].length) throw new TypeError('legalDocumentsApi: nothing to correct');
  const res = await call(path, { method: 'PATCH', body: form }, 'Failed to correct legal version');
  return res.json();
}

/** Brian's copy of a version's PDF, as a Blob (the route needs the bearer token: no plain link). */
export async function downloadLegalPdf(document, version) {
  const res = await call(`${versionPath(document, version)}/pdf`, {}, 'Failed to download legal PDF');
  return res.blob();
}
